/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { observer } from 'mobx-react';
import * as React from 'react';

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { getRichTextHtml } from '@msdyn365-commerce/core';
import { mapProductCollection } from '../../Utilities/product-placement-data-helper';
import { IEventListData } from './event-list.data';
import { IEventListProps } from './event-list.props.autogenerated';

import RenderEventList from '../../Utilities/event-list-render';

// ==============================================================================
// CLASS DEFINITION
// ==============================================================================
/**
 *
 * EventList component
 * @extends {React.Component<IEventListProps<IEventListData>>}
 */
@observer
class EventList extends React.Component<IEventListProps<IEventListData>> {

    // =============================================================================
    // PUBLIC METHODS
    // =============================================================================

    // --------------------------------------------------------------
    // --------------------------------------------------------------
    public render(): JSX.Element {
        const { config, context, data } = this.props;
        const returnedView = this.props.renderView(this.props);
        if(returnedView){
            return returnedView;
        }

        const products: FullProduct[] = this._getEventListItems();

        if (products && products.length) {
            const eventList = mapProductCollection(
                products.filter(product => product.ProductDetails && product.ProductDetails.Name),
                'Free',
                '',
                '',
                '',
                context,
                (data.category && data.category.result) || undefined
            );

            if (eventList && eventList.length !== 0) {
                return (
                    <RenderEventList
                        events={eventList}
                        maximum={config.maxToDisplay}
                        noFutureEventsJSX={this._renderNoEventsMessage()}
                        soldOutText={this.props.resources.events__soldOut}
                        debugMode={this.props.context.request.query && !!this.props.context.request.query.debug}
                        timezone={this.props.app.config.siteTimezone || 'America/Los_Angeles'}
                    />
                );
            } else {
                console.log('Could not map product collection to product placement item succesfully');
            }
        }
        return this._renderNoEventsMessage();
    }

    // =============================================================================
    // JSX FRAGMENTS
    // =============================================================================

    // --------------------------------------------------------------
    // If no events are present, render this instead of the events
    // list
    // --------------------------------------------------------------
    private _renderNoEventsMessage(): JSX.Element {
        const { noEventsText } = this.props.config;

        const textToShow = noEventsText || 'No Events';

        return (
            // tslint:disable-next-line: react-no-dangerous-html
            <div
                className='event-list-empty'
                dangerouslySetInnerHTML={getRichTextHtml(textToShow)}
            />
        );
    }

    // =============================================================================
    // PRIVATE METHODS
    // =============================================================================

    // --------------------------------------------------------------
    // Construct the event list data
    // --------------------------------------------------------------
    private _getEventListItems(): FullProduct[] {
        const { data } = this.props;
        let products: FullProduct[] = [];

        // TODO: May need to add refineSearch in future.
        // const refineSearchProducts = this.props.data.refineSearchProducts && this.props.data.refineSearchProducts.result;
        // if (refineSearchProducts && refineSearchProducts.refinementCriteria && refineSearchProducts.refinementCriteria.length) {
        //     if (refineSearchProducts.productSearchResult && refineSearchProducts.productSearchResult.length) {
        //         products = refineSearchProducts.productSearchResult;
        //     }
        // }

        if (data.products && data.products.result && data.products.result.length > 0) {
            products = data.products.result;
        }

        return products;
    }
}

export default EventList;